/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';

import {
  takeUntil,
  switchMap,
  of,
  debounceTime,
  distinctUntilChanged,
  catchError,
  tap,
  merge,
  map,
} from 'rxjs';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { CommonModule } from '@angular/common';
import { AddressService } from '../../../../shared/services/address/address.service';
import { SearchService } from '../../services/search.service';
import { RecordDetailsComponent } from '../../../../shared/components/record-details/record-details.component';
import {
  State,
  County,
  City,
  Zipcode,
} from '../../../../shared/models/address';
import { NumberValidatorService } from '../../../../shared/services/number-validator/number-validator.service';
import {
  RecordMatch,
  RecordSourceType,
} from '../../models/check-record-existence';

export function pastOrPresentDateValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;

    const selectedDate = new Date(value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return selectedDate > today ? { futureDate: true } : null;
  };
}

@Component({
  selector: 'app-business-detail-form',
  standalone: true,
  imports: [ReactiveFormsModule, RecordDetailsComponent, CommonModule],
  templateUrl: './business-detail-form.component.html',
  styleUrl: './business-detail-form.component.scss',
})
export class BusinessDetailFormComponent extends DestroyBase implements OnInit {
  @Input() initialValue: any;
  @Output() formSubmit = new EventEmitter<any>();

  businessForm: FormGroup;
  submitAttempted = false;
  todayDate = new Date().toISOString().split('T')[0];
  private isInitialPatch = true;

  duplicateBusinesses: RecordMatch[] = [];
  totalDuplicateCount = 0;
  duplicatePage = 1;
  readonly duplicatePageSize = 10;
  hasMoreDuplicates = false;
  isSearchingDuplicates = false;
  isLoadingMoreDuplicates = false;
  selectedCompanyUniqueId: string | null = null;
  activeDuplicateField: 'businessName' | 'companyEmail' | null = null;

  private searchService = inject(SearchService);
  private addressService = inject(AddressService);
  private numberValidatorService = inject(NumberValidatorService);

  states: State[] = [];
  counties: County[] = [];
  cities: City[] = [];
  zipcodes: Zipcode[] = [];

  businessSupportTypes = [
    'Loans and Capital Products',
    'Small Business Technical Assistance',
    'Business Operational Tools',
    'Commercial Real Estate',
    'Solar and Energy Efficiency Products',
    'Finding Investors',
  ];

  businessTypeOptions = [
    { value: 'sole_proprietorship', label: 'Sole Proprietorship' },
    { value: 'lp', label: 'Limited Partnership (LP)' },
    { value: 'llp', label: 'Limited Liability Partnership (LLP)' },
    { value: 'llc', label: 'Limited Liability Company (LLC)' },
    { value: 'c_corporation', label: 'C Corporation' },
    { value: 's_corporation', label: 'S Corporation' },
    { value: 'benefit_corporation', label: 'Benefit Corporation' },
    { value: 'close_corporation', label: 'Close Corporation' },
    { value: 'non_profit_corporation', label: 'Non-Profit Corporation' },
    { value: 'cooperative', label: 'Cooperative' },
    { value: 'other', label: 'Other' },
  ];

  private businessSupportTypeMap: Record<string, string> = {
    'Loans and Capital Products': 'loans_and_capital',
    'Small Business Technical Assistance': 'technical_assistance',
    'Business Operational Tools': 'business_operational_tools',
    'Commercial Real Estate': 'commercial_real_estate',
    'Solar and Energy Efficiency Products': 'solar_and_energy',
    'Finding Investors': 'finding_investors',
  };

  constructor(private fb: FormBuilder) {
    super();
    this.businessForm = this.fb.group({
      businessName: ['', Validators.required],
      address: ['', Validators.required],
      city: ['', Validators.required],
      stateOrProvince: ['', Validators.required],
      county: ['', Validators.required],
      zipCode: ['', Validators.required],
      country: [
        { value: 'United States', disabled: true },
        Validators.required,
      ],

      applicationSummary: ['', Validators.required],
      businessOwnerName: ['', Validators.required],
      phone: ['', [Validators.pattern(/^\+?[\d\s-()]{10,}$/)]],
      companyEmail: ['', [Validators.required, Validators.email]],
      website: [
        '',
        [
          Validators.pattern(
            /^(https?:\/\/)?([\da-z.-]+)\.([\da-z.]{2,6})([/\w .-]*)*\/?$/i,
          ),
        ],
      ],
      businessType: ['', Validators.required],
      preferredLanguage: ['', Validators.required],
      isMilitaryVeteran: ['', Validators.required],
      isVeteranSpouse: ['', Validators.required],
      isMinorityOwned: [''],
      isWomenOwned: [''],
      businessSupportType: this.fb.group(
        this.businessSupportTypes.reduce((acc, type) => {
          acc[this.sanitizeKey(type)] = [false];
          return acc;
        }, {} as any),
        { validators: this.atLeastOneCheckboxValidator() },
      ),
      interestedInChamberOrBusinessNetworking: ['', Validators.required],
      yearFounded: ['', [Validators.required, pastOrPresentDateValidator()]],
      numberOfEmployees: [
        '',
        [
          Validators.required,
          this.numberValidatorService.numberValidator('numberOfEmployees'),
        ],
      ],
    });
    this.setupStateChangeListener();
    this.setupCityChangeListener();
  }

  ngOnInit(): void {
    this.loadStates();
    this.setupDuplicateCheckListener();
    if (this.initialValue) {
      const patchData = { ...this.initialValue };

      // Convert selectedSupportTypes or selectedSupportTypesBackend array back to businessSupportType checkbox object
      // Try selectedSupportTypes first (display names), then selectedSupportTypesBackend (backend values)
      if (Array.isArray(patchData.selectedSupportTypes)) {
        const businessSupportTypeObj: Record<string, boolean> = {};
        this.businessSupportTypes.forEach((type) => {
          const key = this.sanitizeKey(type);
          businessSupportTypeObj[key] =
            patchData.selectedSupportTypes.includes(type);
        });
        patchData.businessSupportType = businessSupportTypeObj;
      } else if (Array.isArray(patchData.selectedSupportTypesBackend)) {
        const businessSupportTypeObj: Record<string, boolean> = {};
        this.businessSupportTypes.forEach((type) => {
          const key = this.sanitizeKey(type);
          const backendValue = this.businessSupportTypeMap[type];
          businessSupportTypeObj[key] =
            patchData.selectedSupportTypesBackend.includes(backendValue);
        });
        patchData.businessSupportType = businessSupportTypeObj;
      }

      this.businessForm.patchValue(patchData);
    }
    this.isInitialPatch = false;
  }

  private setupDuplicateCheckListener(): void {
    const businessNameControl = this.businessForm.get('businessName');
    const emailControl = this.businessForm.get('companyEmail');

    if (!businessNameControl || !emailControl) return;

    const businessName$ = businessNameControl.valueChanges.pipe(
      map((val) => ({
        field: 'businessName' as const,
        value: (val || '').trim(),
      })),
    );
    const email$ = emailControl.valueChanges.pipe(
      map((val) => ({
        field: 'companyEmail' as const,
        value: (val || '').trim(),
      })),
    );

    merge(businessName$, email$)
      .pipe(
        takeUntil(this.destroy$),
        tap(({ field, value }) => {
          const isFieldValid =
            (field === 'businessName' && value.length >= 2) ||
            (field === 'companyEmail' && this.isValidEmail(value));

          if (isFieldValid) {
            this.activeDuplicateField = field;
          } else {
            this.isSearchingDuplicates = false;
            this.duplicateBusinesses = [];
            this.totalDuplicateCount = 0;
            this.hasMoreDuplicates = false;
            if (this.activeDuplicateField === field) {
              this.activeDuplicateField = null;
            }
          }
        }),
        debounceTime(350),
        map(() => {
          const businessName = (businessNameControl.value || '').trim();
          const email = (emailControl.value || '').trim();
          return { businessName, email };
        }),
        distinctUntilChanged(
          (prev, curr) =>
            prev.businessName === curr.businessName &&
            prev.email === curr.email,
        ),
        switchMap(({ businessName, email }) => {
          if (!this.activeDuplicateField) {
            this.duplicateBusinesses = [];
            this.totalDuplicateCount = 0;
            this.hasMoreDuplicates = false;
            this.isSearchingDuplicates = false;
            return of(null);
          }

          const bName = businessName.length >= 2 ? businessName : undefined;
          const em = this.isValidEmail(email) ? email : undefined;

          if (!bName && !em) {
            this.duplicateBusinesses = [];
            this.totalDuplicateCount = 0;
            this.hasMoreDuplicates = false;
            this.isSearchingDuplicates = false;
            this.activeDuplicateField = null;
            return of(null);
          }

          this.isSearchingDuplicates = true;
          this.duplicatePage = 1;

          return this.searchService
            .checkRecordExistence(bName, em, 1, this.duplicatePageSize)
            .pipe(
              catchError((err) => {
                console.error('Error checking duplicate records:', err);
                return of(null);
              }),
            );
        }),
      )
      .subscribe({
        next: (res) => {
          this.isSearchingDuplicates = false;
          if (res && res.results && res.results.length > 0) {
            this.duplicateBusinesses = res.results;
            this.totalDuplicateCount = res.count ?? res.results.length;
            this.hasMoreDuplicates =
              !!res.next ||
              this.duplicateBusinesses.length < this.totalDuplicateCount;
          } else {
            this.duplicateBusinesses = [];
            this.totalDuplicateCount = 0;
            this.hasMoreDuplicates = false;
          }
        },
        error: () => {
          this.isSearchingDuplicates = false;
          this.duplicateBusinesses = [];
          this.totalDuplicateCount = 0;
          this.hasMoreDuplicates = false;
        },
      });
  }

  private isValidEmail(email?: string | null): boolean {
    if (!email) return false;
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email.trim());
  }

  onDuplicateListScroll(event: Event): void {
    const element = event.target as HTMLElement;
    if (!element) return;

    const threshold = 25;
    const isAtBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight <=
      threshold;

    if (
      isAtBottom &&
      this.hasMoreDuplicates &&
      !this.isLoadingMoreDuplicates &&
      !this.isSearchingDuplicates
    ) {
      this.loadMoreDuplicates();
    }
  }

  loadMoreDuplicates(): void {
    if (this.isLoadingMoreDuplicates || !this.hasMoreDuplicates) return;

    const businessNameControl = this.businessForm.get('businessName');
    const emailControl = this.businessForm.get('companyEmail');

    const businessName = (businessNameControl?.value || '').trim();
    const email = (emailControl?.value || '').trim();

    const bName = businessName.length >= 2 ? businessName : undefined;
    const em = this.isValidEmail(email) ? email : undefined;

    if (!bName && !em) return;

    this.isLoadingMoreDuplicates = true;
    const nextPage = this.duplicatePage + 1;

    this.searchService
      .checkRecordExistence(bName, em, nextPage, this.duplicatePageSize)
      .pipe(
        takeUntil(this.destroy$),
        catchError((err) => {
          console.error('Error loading more duplicate records:', err);
          return of(null);
        }),
      )
      .subscribe({
        next: (res) => {
          this.isLoadingMoreDuplicates = false;
          if (res && res.results && res.results.length > 0) {
            this.duplicatePage = nextPage;
            const existingIds = new Set(
              this.duplicateBusinesses.map((b) => b.unique_id || b.id),
            );
            const newResults = res.results.filter(
              (b) => !existingIds.has(b.unique_id || b.id),
            );
            this.duplicateBusinesses = [
              ...this.duplicateBusinesses,
              ...newResults,
            ];
            this.totalDuplicateCount =
              res.count ?? this.duplicateBusinesses.length;
            this.hasMoreDuplicates =
              !!res.next ||
              this.duplicateBusinesses.length < this.totalDuplicateCount;
          } else {
            this.hasMoreDuplicates = false;
          }
        },
        error: () => {
          this.isLoadingMoreDuplicates = false;
        },
      });
  }

  formatSourceLabel(sourceType?: number): string {
    switch (sourceType) {
      case RecordSourceType.CRF:
        return 'WNCSBI';
      case RecordSourceType.CRM_DNB:
        return 'CRM DNB';
      case RecordSourceType.CUSTOM:
        return 'Referral';
      case RecordSourceType.DATA_AXLE:
        return 'Data Axle';
      case RecordSourceType.PUBLIC:
        return 'Inquiries';
      case RecordSourceType.SME:
        return 'Business Record';
      case RecordSourceType.VLF:
        return 'VLF';
      default:
        return sourceType ? `Source ${sourceType}` : '';
    }
  }

  getSourceBadgeClass(sourceType?: number): string {
    switch (sourceType) {
      case RecordSourceType.CRF:
        return 'crf';
      case RecordSourceType.CRM_DNB:
        return 'dnb';
      case RecordSourceType.CUSTOM:
        return 'custom';
      case RecordSourceType.DATA_AXLE:
        return 'data-axle';
      case RecordSourceType.PUBLIC:
        return 'public';
      case RecordSourceType.SME:
        return 'sme';
      case RecordSourceType.VLF:
        return 'vlf';
      default:
        return 'default';
    }
  }

  viewCompanyDetails(match: RecordMatch, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.selectedCompanyUniqueId = match.unique_id;
  }

  formatMatchAddress(match: RecordMatch): string {
    return [match.city, match.state].filter(Boolean).join(', ');
  }

  formatMatchReason(reason: string): string {
    if (!reason) return '';
    switch (reason.toLowerCase().trim()) {
      case 'similar_name':
        return 'Similar Name';
      case 'email':
        return 'Email';
      case 'phone':
        return 'Phone';
      default:
        return reason
          .replace(/_/g, ' ')
          .toLowerCase()
          .replace(/\b\w/g, (c) => c.toUpperCase());
    }
  }

  closeCompanyDetails(): void {
    this.selectedCompanyUniqueId = null;
  }

  private loadStates() {
    this.addressService
      .getStates()
      .pipe(
        takeUntil(this.destroy$),
        switchMap((states) => {
          this.states = states;

          if (this.initialValue) {
            this.businessForm.patchValue(this.initialValue, {
              emitEvent: false,
            });
          }

          return this.loadAddressHierarchy();
        }),
      )
      .subscribe({
        next: (zips) => {
          this.zipcodes = zips;
        },
        error: (err) => console.error('Failed to load states', err),
      });
  }

  private loadAddressHierarchy() {
    const stateName = this.businessForm.get('stateOrProvince')?.value;
    const countyName = this.businessForm.get('county')?.value;
    const cityName = this.businessForm.get('city')?.value;

    const selectedState = this.states.find((s) => s.name === stateName);
    if (!selectedState) return of([]);

    return this.addressService
      .getCitiesAndCountiesByStateIds([selectedState.id])
      .pipe(
        switchMap(({ counties, cities }) => {
          this.counties = counties;
          this.cities = cities;
          const selectedCounty = counties.find(
            (c) => c.name.toLowerCase() === countyName?.toLowerCase(),
          );
          if (!selectedCounty) return of([]);
          const selectedCity = cities.find(
            (c) => c.name.toLowerCase() === cityName?.toLowerCase(),
          );
          if (!selectedCity) return of([]);
          return this.addressService.getZipcodes(selectedCity.id);
        }),
      );
  }

  private setupStateChangeListener() {
    this.businessForm
      .get('stateOrProvince')
      ?.valueChanges.pipe(
        takeUntil(this.destroy$),
        switchMap((stateName) => {
          if (this.isInitialPatch || !stateName)
            return of({ counties: [], cities: [] });

          this.counties = [];
          this.cities = [];
          this.zipcodes = [];

          this.businessForm.patchValue(
            { county: '', city: '', zipCode: '' },
            { emitEvent: false },
          );

          const selectedState = this.states.find((s) => s.name === stateName);
          if (!selectedState) return of({ counties: [], cities: [] });

          return this.addressService.getCitiesAndCountiesByStateIds([
            selectedState.id,
          ]);
        }),
      )
      .subscribe(({ counties, cities }) => {
        this.counties = counties;
        this.cities = cities;
      });
  }

  private setupCityChangeListener() {
    this.businessForm
      .get('city')
      ?.valueChanges.pipe(
        takeUntil(this.destroy$),
        switchMap((cityName) => {
          if (this.isInitialPatch || !cityName) return of([]);

          this.zipcodes = [];
          this.businessForm.patchValue({ zipCode: '' }, { emitEvent: false });

          const selectedCity = this.cities.find(
            (c) => c.name.toLowerCase() === cityName.toLowerCase(),
          );

          if (!selectedCity) return of([]);

          return this.addressService.getZipcodes(selectedCity.id);
        }),
      )
      .subscribe((zips) => {
        this.zipcodes = zips;
      });
  }

  sanitizeKey(type: string): string {
    return type.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  atLeastOneCheckboxValidator() {
    return (group: any) => {
      const values = Object.values(group.value || {});
      const hasAtLeastOne = values.some((val) => val === true);
      return hasAtLeastOne ? null : { required: true };
    };
  }

  get hasErrors(): boolean {
    return this.submitAttempted && this.businessForm.invalid;
  }

  public onSubmitForm() {
    if (this.businessForm.valid) {
      const formValue = this.businessForm.value;
      const selectedSupportTypes = this.extractSelectedSupportTypes(
        formValue.businessSupportType,
      );
      const selectedSupportTypesBackend =
        this.extractSelectedSupportTypesBackend(formValue.businessSupportType);
      this.formSubmit.emit({
        ...formValue,
        country: 'United States',
        selectedSupportTypes, // Display names for form flow
        selectedSupportTypesBackend, // Backend values for API submission
      });
    } else {
      this.submitAttempted = true;
      this.markFormGroupTouched(this.businessForm);
    }
  }

  private extractSelectedSupportTypes(
    supportTypes: Record<string, boolean>,
  ): string[] {
    if (!supportTypes) return [];

    return Object.entries(supportTypes)
      .filter(([, selected]) => selected)
      .map(([key]) => {
        return (
          this.businessSupportTypes.find(
            (type) => this.sanitizeKey(type) === key,
          ) || key
        );
      });
  }

  private extractSelectedSupportTypesBackend(
    supportTypes: Record<string, boolean>,
  ): string[] {
    if (!supportTypes) return [];

    return Object.entries(supportTypes)
      .filter(([, selected]) => selected)
      .map(([key]) => {
        const displayLabel = this.businessSupportTypes.find(
          (type) => this.sanitizeKey(type) === key,
        );
        return displayLabel ? this.businessSupportTypeMap[displayLabel] : key;
      });
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();
    });
  }

  formatCityName(name: string): string {
    if (!name) return '';

    return name
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}
